// Centralized API service for all HTTP requests
// All data fetches go through clientDataService which provides caching and auth
import { getClientType } from '../services/clientConfig';
import { fetchClientJSON, fetchClientText } from '../services/clientDataService';
import { updateTraffixData } from '../components/common/traffixLoadingData';

// ==================== Traffic Data APIs ====================

/**
 * Parse CSV text to JSON array
 * @param {string} csvText - CSV text data
 * @returns {Array} - Array of objects
 */
const parseCSV = (csvText) => {
  const lines = csvText.trim().split('\n');
  if (lines.length === 0) return [];

  const headers = lines[0].split(',');
  const result = [];

  for (let i = 1; i < lines.length; i++) {
    const values = lines[i].split(',');
    const obj = {};
    headers.forEach((header, index) => {
      const value = values[index];
      const headerName = header.trim();

      // Keep certain fields as strings (dates, IDs, times)
      if (headerName === 'day_group_label' || headerName === 'UnifiedID' ||
          headerName === 't_start' || headerName === 't_end') {
        obj[headerName] = value;
      } else {
        // Try to parse numbers for other fields
        const numValue = parseFloat(value);
        obj[headerName] = isNaN(numValue) ? value : numValue;
      }
    });
    result.push(obj);
  }

  return result;
};

/**
 * Fetch traffic data from metrics.csv
 * @returns {Promise<Array>} - Metrics data
 */
export const fetchMetricsData = async () => {
  const clientType = getClientType();
  try {
    const csvText = await fetchClientText(clientType, 'metrics.csv');
    const data = parseCSV(csvText);
    updateTraffixData(data);
    return data;
  } catch (error) {
    console.error('Error loading metrics.csv, falling back to metrics.json:', error);
    const data = await fetchClientJSON(clientType, 'metrics.json');
    updateTraffixData(data);
    return data;
  }
};

export const fetchTrafficCSV = () => fetchMetricsData();
export const fetchTrafficZoneData = () => fetchMetricsData();

/**
 * Fetch network offset data (converted from GPKG to GeoJSON)
 * @returns {Promise<object>} - Network offset data as GeoJSON
 */
export const fetchNetworkOffsetData = async () => {
  const clientType = getClientType();
  try {
    console.log(`Loading network_offset.geojson for client: ${clientType}...`);

    const geojson = await fetchClientJSON(clientType, 'network_offset.geojson');

    console.log('Successfully loaded network_offset.geojson:', {
      features: geojson.features?.length || 0,
      type: geojson.type,
      client: clientType
    });

    // Map the GPKG attributes to match expected format
    if (geojson.features) {
      geojson.features = geojson.features.map(feature => ({
        ...feature,
        properties: {
          ...feature.properties,
          hash: feature.properties.UnifiedID,
          StreetName: feature.properties.StreetName,
          SpeedLimit: feature.properties.SpeedLimit,
          Length: feature.properties.Length_sum,
          FRC: feature.properties.FRC,
          'Segment Id': feature.properties.UnifiedID
        }
      }));
      
      // Update global Screensaver configurations with network properties
      updateTraffixData(geojson.features.map(f => f.properties));
    }

    return geojson;

  } catch (error) {
    console.error('Error loading network_offset.geojson:', error);
    console.warn('Falling back to archi_zona.geojson...');

    return fetchClientJSON(clientType, 'archi_zona.geojson')
      .catch(fallbackError => {
        console.error('Error loading fallback GeoJSON:', fallbackError);
        throw fallbackError;
      });
  }
};

export const fetchMapGeoJSON = () => fetchNetworkOffsetData();
export const fetchArchiZonaGeoJSON = () => fetchNetworkOffsetData();

// ==================== Environmental Data APIs ====================

export const fetchEnvironmentData = () =>
  fetchClientJSON(getClientType(), 'Environment_1h.json');

export const fetchEnvironmentDailyData = (date) =>
  fetchClientJSON(getClientType(), `ambientali/${date}.json`);

export const fetchEnvironmentIndex = () =>
  fetchClientJSON(getClientType(), 'ambientali/media_giornaliera.json');

export const fetchEnvironmentFileIndex = () =>
  fetchClientJSON(getClientType(), 'ambientali/media_giornaliera.json');

// ==================== Noise/Audio Data APIs ====================

export const fetchNoiseData = () =>
  fetchClientJSON(getClientType(), 'Noise_1min.json');

export const fetchDetectedSounds = () =>
  fetchClientJSON(getClientType(), 'detected_sounds.json');

export const fetchNoiseDailyData = (date) =>
  fetchClientJSON(getClientType(), `geojson/${date}.json`);

export const fetchNoiseIndex = () =>
  fetchClientJSON(getClientType(), 'geojson/json_index.json');

// ==================== Vibration Data APIs ====================

export const fetchVibrationData = () =>
  fetchClientJSON(getClientType(), 'Vibration_1min.json');

export const fetchIMUData = (filename) =>
  fetchClientJSON(getClientType(), `imu/${filename}`);

export const fetchIMUIndex = () =>
  fetchClientJSON(getClientType(), 'imu/index.json');

// ==================== Vehicle/Trip Data APIs ====================

export const fetchAnimatedVehicles = () =>
  fetchClientJSON(getClientType(), 'animated_vehicles.geojson');

export const fetchTripsData = () =>
  fetchClientJSON(getClientType(), 'trips_deckgl.json');

// ==================== Point Cloud Data APIs ====================

export const fetchPointCloud3D = () =>
  fetchClientJSON(getClientType(), 'NuvolaPunti3d.json');

// ==================== Building Data APIs ====================

export const fetchBuildingsData = () =>
  fetchClientJSON(getClientType(), 'buildings_genova.json');

// ==================== Helper Functions ====================

export const fetchWithFallback = async (primaryFetch, fallbackFetch) => {
  try {
    return await primaryFetch();
  } catch (error) {
    console.warn('Primary fetch failed, trying fallback...', error);
    return await fallbackFetch();
  }
};

export const batchFetch = async (fetchFunctions) => {
  return Promise.all(fetchFunctions.map(fn => fn().catch(err => {
    console.error('Batch fetch error:', err);
    return null;
  })));
};
